import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { Button, Dialog, Divider, IconButton, Typography } from '@mui/material';
import OrganisationTreeNode from 'shared/features/organisation/models/organisation-tree-node';
import { OrganisationUnitTreeProvider } from 'shared/features/organisation/providers/organisation-unit-tree-provider';

import AlternativeDiversionOrganisationUnit from 'features/organisation/domain/models/alternative-diversion-organisation-unit';
import MultiSelectAlternativeDiversionTree from 'features/organisation/views/multi-select-alternative-diversion-tree/multi-select-alternative-diversion-tree.component';
import styles from './organisation-details-alternative-diversion-modal.module.scss';

type Props = {
  organisationUnitId: string;
  isOpen: boolean;
  selectedAlternativeDiversions: AlternativeDiversionOrganisationUnit[];
  onClose: () => void;
  onSaveButtonClicked: (selectedAlternativeDiversions: AlternativeDiversionOrganisationUnit[]) => void;
};

const OrganisationDetailsAlternativeDiversionModal = (props: Readonly<Props>): React.JSX.Element => {
  const { t } = useTranslation('organisation');

  const [selectedAlternativeDiversions, setSelectedAlternativeDiversions] = useState<AlternativeDiversionOrganisationUnit[]>(
    props.selectedAlternativeDiversions,
  );

  useEffect(() => {
    setSelectedAlternativeDiversions(props.selectedAlternativeDiversions);
  }, [props.selectedAlternativeDiversions]);

  const handleOnClose = (): void => {
    props.onClose();
    setSelectedAlternativeDiversions(props.selectedAlternativeDiversions);
  };

  const handleOnSaveButtonClicked = (): void => {
    props.onSaveButtonClicked(selectedAlternativeDiversions);
  };

  const getBreadCrumbs = (node: OrganisationTreeNode): string[] => {
    const breadCrumbs: string[] = [];
    let currentNode: OrganisationTreeNode | undefined = node;

    while (currentNode) {
      breadCrumbs.unshift(currentNode.name);
      currentNode = currentNode.parentOrganisationUnit;
    }

    return breadCrumbs;
  };

  const onSelectOrganisationUnit = (selectedNode: OrganisationTreeNode): void => {
    const selectedOrganisationUnitsCopy = [...selectedAlternativeDiversions];
    const indexOfNode = selectedOrganisationUnitsCopy.findIndex((x) => x.id === selectedNode.id);

    const selectedAlternativeDiversion: AlternativeDiversionOrganisationUnit = {
      id: selectedNode.id,
      name: selectedNode.name,
      breadCrumbs: getBreadCrumbs(selectedNode),
    };

    if (indexOfNode === -1) {
      selectedOrganisationUnitsCopy.push(selectedAlternativeDiversion);
    } else {
      selectedOrganisationUnitsCopy.splice(indexOfNode, 1);
    }

    removeChildrenFromSelectedOrganisationUnits(selectedOrganisationUnitsCopy, selectedNode.childOrganisationUnits);

    setSelectedAlternativeDiversions(selectedOrganisationUnitsCopy);
  };

  const removeChildrenFromSelectedOrganisationUnits = (
    selectedOrganisationUnitsCopy: AlternativeDiversionOrganisationUnit[],
    childOrganisationUnits: OrganisationTreeNode[],
  ): void => {
    childOrganisationUnits.forEach((child) => {
      const indexOfNode = selectedOrganisationUnitsCopy.findIndex((x) => x.id === child.id);

      if (indexOfNode !== -1) {
        selectedOrganisationUnitsCopy.splice(indexOfNode, 1);
      }

      removeChildrenFromSelectedOrganisationUnits(selectedOrganisationUnitsCopy, child.childOrganisationUnits);
    });
  };

  return (
    <Dialog
      className={styles.organisationDetailsLinkableAlternativeDiversionModal}
      open={props.isOpen}
      onClose={handleOnClose}
      slotProps={{
        paper: {
          classes: { root: styles.linkableAlternativeDiversionModalPaper },
        },
      }}
    >
      <div className={styles.linkableAlternativeDiversionModalHeaderContainer}>
        <Typography variant="h2">{t('linkableAlternativeDiversionModal.title')}</Typography>
        <div className={styles.actions}>
          {selectedAlternativeDiversions.length < 1 && (
            <Button variant="contained" disabled startIcon={<CheckIcon className={styles.checkIcon} />}>
              {t('linkableAlternativeDiversionModal.saveButton')}
            </Button>
          )}
          {selectedAlternativeDiversions.length > 0 && (
            <Button
              className={styles.linkButton}
              variant="contained"
              onClick={handleOnSaveButtonClicked}
              startIcon={<CheckIcon className={styles.checkIcon} />}
              data-testid="linkselectedAlternativeDiversionsButton"
            >
              {t('linkableAlternativeDiversionModal.saveButton')}
            </Button>
          )}

          <Divider orientation="vertical" flexItem className={styles.actionsDivider} />
          <IconButton aria-label="close" className={styles.closeButton} onClick={handleOnClose}>
            <CloseIcon />
          </IconButton>
        </div>
      </div>
      <Divider className={styles.tableDivider} />
      <OrganisationUnitTreeProvider.ForLocation organisationUnitId={props.organisationUnitId}>
        <MultiSelectAlternativeDiversionTree
          onSelectOrganisationUnit={onSelectOrganisationUnit}
          selectedOrganisationUnitIds={selectedAlternativeDiversions.map((x) => x.id)}
          disabledChildrenOfOrganisationUnitIds={[props.organisationUnitId]}
        />
      </OrganisationUnitTreeProvider.ForLocation>
    </Dialog>
  );
};

export default OrganisationDetailsAlternativeDiversionModal;
